import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatInput} from "@angular/material/input";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {NgForOf} from "@angular/common";
import {IDropdownOption} from "../../interfaces/IDropDownOptions";
import {MatAutocomplete, MatAutocompleteTrigger} from "@angular/material/autocomplete";
import {map, startWith} from "rxjs";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";

@Component({
  selector: 'app-filterable-dropdown',
  standalone: true,
  imports: [
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    FormsModule,
    NgForOf,
    MatLabel,
    ReactiveFormsModule,
    MatAutocompleteTrigger,
    MatAutocomplete,
    MatIcon,
    MatIconButton,
    MatSuffix
  ],
  templateUrl: './filterable-dropdown.component.html',
  styleUrl: './filterable-dropdown.component.css'
})
export class FilterableDropdownComponent implements OnInit{

  @Output() selectionChange: EventEmitter<string> = new EventEmitter<string>();

  /**
   * Emitted when the clear button empties the box. Clearing used to only wipe the text, leaving the screen
   * still filtered by a charity whose name was no longer on display — so on the payouts screen the list looked
   * like the whole game's and was not.
   */
  @Output() selectionCleared: EventEmitter<void> = new EventEmitter<void>();

  @Input() placeholder: string = '';

  @Input() set optionsSetter(dropdownOptions: IDropdownOption[]) {
    this.dropdownOptions = dropdownOptions;
    this.filteredOptions = dropdownOptions;
    // If a selectedId was set before options were loaded, apply it now
    this.applySelectedId();
  }

  @Input() set selectedId(id: string | null) {
    this._selectedId = id;
    this.applySelectedId();
  }

  private _selectedId: string | null = null;

  myControl = new FormControl();
  filteredOptions: IDropdownOption[] = [];
  dropdownOptions: IDropdownOption[] = [];

  /**
   * Writes only when the box is not already showing what the parent says is selected.
   *
   * The guard is the point: a parent that binds selectedId echoes back the id this component just emitted, and
   * an unconditional setValue on that echo is a second value change for one selection. The input is also the
   * filter and is still focused, so that echo re-filtered the option list to the name just chosen and left the
   * autocomplete open — meaning the next click on the box raised no focus event, onFocus never ran, and the
   * list stayed narrowed to one option. On the payouts filter that hid "All charities", the way back.
   */
  private applySelectedId(): void {
    if (this._selectedId && this.dropdownOptions.length > 0) {
      const selectedOption = this.dropdownOptions.find(opt => opt.id === this._selectedId);
      if (selectedOption && this.myControl.value !== selectedOption.name) {
        this.myControl.setValue(selectedOption.name);
      }
    } else if (!this._selectedId && this.myControl.value) {
      this.myControl.setValue('');
    }
  }

  ngOnInit(): void {
    this.myControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
        return filterValue ? this.dropdownOptions.filter(option => option.name.toLowerCase().includes(filterValue)) : this.dropdownOptions;
      })
    ).subscribe(options => {
      this.filteredOptions = options;
    });
  }

  /**
   * Reopening the box offers every option again, not just the ones matching what is already in it.
   *
   * Selecting an option puts its name in the input, and the input is also the filter — so on reopening, the
   * only option on offer was the one already selected. On the payouts screen that made the "All charities"
   * entry unreachable the moment a charity had been chosen: the way back was to delete the text, which is not
   * a thing the list told you to do. Typing filters as before.
   */
  onFocus(): void {
    this.filteredOptions = this.dropdownOptions;
  }

  onOptionSelected(event: any): void {
    const selectedOption = event.option.value as IDropdownOption;
    this.myControl.setValue(selectedOption.name);
    this.selectionChange.emit(selectedOption.id);
  }

  resetSelection(): void {
    this.myControl.setValue('');
    this.selectionCleared.emit();
  }
}
